import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, XCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../slices/types';
import { 
  createGameRate, 
  clearCreateGameRateError, 
  clearLastCreatedGameRate,
  type CreateGameRatePayload 
} from '../../slices/GameRates/GameRateSlice';

interface GameRate {
  open: string;
  close: string;
  jodi: string;
  singlePana: string;
  doublePana: string;
  triplePana: string;
  halfSangam: string;
  fullSangam: string;
}

const GameRates: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    createGameRateLoading, 
    createGameRateError, 
    lastCreatedGameRate 
  } = useAppSelector((state) => state.GameRate);

  const [gameRates, setGameRates] = useState<GameRate>({
    open: '',
    close: '',
    jodi: '',
    singlePana: '',
    doublePana: '',
    triplePana: '',
    halfSangam: '',
    fullSangam: ''
  });

  const [savingField, setSavingField] = useState<keyof GameRate | null>(null);

  // Clear success messages after 3 seconds
  useEffect(() => {
    if (lastCreatedGameRate) {
      const timer = setTimeout(() => {
        dispatch(clearLastCreatedGameRate());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [lastCreatedGameRate, dispatch]);

  const handleInputChange = (field: keyof GameRate, value: string) => {
    setGameRates(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Map field keys to game_name values
  const gameNameMap: Record<keyof GameRate, string> = {
    open: 'Open',
    close: 'Close',
    jodi: 'Jodi',
    singlePana: 'Single Pana',
    doublePana: 'Double Pana',
    triplePana: 'Triple Pana',
    halfSangam: 'Half Sangam',
    fullSangam: 'Full Sangam'
  };

  const handleSaveSingle = async (field: keyof GameRate) => {
    const value = gameRates[field];
    
    // Validate field is filled
    if (!value || value.trim() === '') {
      alert(`Please enter a value for ${gameNameMap[field]}`);
      return;
    }

    const displayRatio = parseFloat(value);
    
    // Validate the value is a valid positive number
    if (isNaN(displayRatio) || displayRatio <= 0) {
      alert(`Please enter a valid positive number for ${gameNameMap[field]}`);
      return;
    }

    // Clear previous errors
    dispatch(clearCreateGameRateError());
    setSavingField(field);

    try {
      const payload: CreateGameRatePayload = {
        game_name: gameNameMap[field],
        display_ratio: displayRatio,
        is_active: true
      };

      // Call POST API for this single game rate
      await dispatch(createGameRate(payload)).unwrap();
      console.log(`${gameNameMap[field]} saved successfully`);
    } catch (error: any) {
      console.error(`Failed to save ${gameNameMap[field]}:`, error);
      // Error messages are handled by createGameRateError state
    } finally {
      setSavingField(null);
    }
  };

  const gameRateFields = [
    { key: 'open' as keyof GameRate, label: 'Open', placeholder: 'Enter ratio for Open (Rs. 1)' },
    { key: 'close' as keyof GameRate, label: 'Close', placeholder: 'Enter ratio for Close (Rs. 1)' },
    { key: 'jodi' as keyof GameRate, label: 'Jodi', placeholder: 'Enter ratio for Jodi (Rs. 1)' },
    { key: 'singlePana' as keyof GameRate, label: 'Single Pana', placeholder: 'Enter ratio for Single Pana (Rs. 1)' },
    { key: 'doublePana' as keyof GameRate, label: 'Double Pana', placeholder: 'Enter ratio for Double Pana (Rs. 1)' },
    { key: 'triplePana' as keyof GameRate, label: 'Triple Pana', placeholder: 'Enter ratio for Triple Pana (Rs. 1)' },
    { key: 'halfSangam' as keyof GameRate, label: 'Half Sangam', placeholder: 'Enter ratio for Half Sangam (Rs. 1)' },
    { key: 'fullSangam' as keyof GameRate, label: 'Full Sangam', placeholder: 'Enter ratio for Full Sangam (Rs. 1)' }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Game Rates</h1>
        </div>
      </div>

      {/* Success Message */}
      {lastCreatedGameRate && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Game Rate Created Successfully!</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>{lastCreatedGameRate.message}</p>
                <p>Game: {lastCreatedGameRate.game_ratio.game_name} - Ratio: {lastCreatedGameRate.game_ratio.display_ratio}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {createGameRateError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Creating Game Rate</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{createGameRateError}</p>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => dispatch(clearCreateGameRateError())}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Win Ratio Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Game Win Ratio For All Bids</h2>
        
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="p-6">
            <div className="space-y-4">
              {gameRateFields.map((field, index) => (
                <div key={field.key} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-6">
                    <label className="text-sm font-medium text-gray-700 w-48 whitespace-nowrap">
                      {field.label} Rs. 1 =
                    </label>
                    <div className="flex-1 max-w-xs">
                      <input
                        type="number"
                        value={gameRates[field.key]}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleSaveSingle(field.key)}
                        disabled={createGameRateLoading || savingField === field.key}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Save className="h-4 w-4" />
                        {savingField === field.key ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameRates;

